/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { TableColumn } from 'components/Table';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import { TablePresets } from 'components';
import useAuth from 'contexts/AuthContext';
import { useSports } from 'contexts/SportContext';
import { ColumnIds } from 'enums';
import { PaginationParams } from 'enums/paginationParams';
import { getSimplifiedTrackingStatusDescriptionByName } from 'features/proposal/components/SimplifiedProposal/IngestionProposal.utils';
import {
  clearStorageKey,
  getPaginationState,
  getStorageValue,
  setStorageValue,
  StorageKeys,
  storeActivePreset,
} from 'utils/local.storage.utils';

import { usePaginationQueryParams } from './usePaginationQueryParams';

// Using a current version to clean state on new deploys;
const tableStateHookVersion = 0.3;

// Updates an array of values on place with defaults
const updateStoredValues = (stored: Array<any>, defaults: Array<any>) => {
  if (stored) {
    defaults?.forEach(filter => {
      const storedIndex = stored.findIndex(stored => stored.columnId === filter.columnId);
      if (storedIndex !== -1) {
        stored[storedIndex] = filter;
      }
    });

    return stored;
  }

  return defaults;
};

interface StoredPaginationFilters {
  key: string;
  columns: TableColumn[];
  defaultFilters?: Array<PaginationFilter>;
  defaultSorts?: Array<PaginationSort>;
  defaultPage?: number;
  defaultPageSize?: number;
  disableControl?: boolean;
  disablePaginatedQueryParams?: boolean;
}

export const useStoredTableState = ({
  key,
  columns,
  defaultFilters = [],
  defaultSorts = [],
  defaultPage = 1,
  defaultPageSize = 20,
  disableControl,
  disablePaginatedQueryParams = false,
}: StoredPaginationFilters) => {
  const {
    user: { tablePresets },
  } = useAuth();

  const {
    paginationQueryParams,
    handlePaginationQueryParamsChange,
    filterQueryParamsParse,
    sortByQueryParamsParse,
  } = usePaginationQueryParams();

  const { sportId } = useSports();
  const isIngestion = window.location.pathname.includes('/ingestion');

  const mapIngestionStatusFiltersToDescriptions = useCallback(
    (inputFilters: PaginationFilter[] = []) =>
      (inputFilters || []).map(filter => {
        if (!isIngestion || filter?.columnId !== ColumnIds.Status) {
          return filter;
        }

        const descriptionValue = filter.value
          .toString()
          .split(',')
          .map(status => getSimplifiedTrackingStatusDescriptionByName(status.trim()))
          .join(',');

        return {
          ...filter,
          value: descriptionValue,
        };
      }),
    [isIngestion],
  );

  const [presets, setPresets] = useState<TablePreset[]>(() => {
    const stored = getStorageValue(key, StorageKeys.Presets) as TablePreset[];
    const tablePresetsItem = tablePresets[key] || [];
    return stored ? [...tablePresetsItem, ...stored] : [...tablePresetsItem];
  });

  const [activePresetId, setActivePresetId] = useState<number>(() =>
    getStorageValue(key, StorageKeys.ActivePreset),
  );

  // Clears the storage key if current version of the hook differs of the stored one
  useEffect(() => {
    const currentStoredVersion = getStorageValue(StorageKeys.TableStateHookVersion);
    if (currentStoredVersion !== tableStateHookVersion) {
      clearStorageKey(key);
      setStorageValue(StorageKeys.TableStateHookVersion, tableStateHookVersion);
    }
  }, [key]);

  const getStoredFilters = useCallback(() => {
    const presetColumns = presets.find(p => p.id === activePresetId)?.columns;
    const stored = getPaginationState(key)?.filters;
    const predefinedFilters = presets.find(p => p.id === activePresetId)?.filters;
    let filters;
    if (stored) {
      filters = updateStoredValues(stored, defaultFilters);
    } else {
      filters = updateStoredValues(predefinedFilters, defaultFilters);
    }

    return mapIngestionStatusFiltersToDescriptions(
      filters.filter(f => (presetColumns ? presetColumns.includes(f.columnId) : true)),
    );
  }, [activePresetId, defaultFilters, key, mapIngestionStatusFiltersToDescriptions, presets]);

  const getStoredSorts = useCallback(() => {
    const stored = getPaginationState(key)?.sortBy;
    const predefinedOrders = presets.find(p => p.id === activePresetId)?.sorts;
    if (stored) {
      return updateStoredValues(stored, defaultSorts);
    }

    return updateStoredValues(predefinedOrders, defaultSorts);
  }, [activePresetId, defaultSorts, key, presets]);

  const [storedFilters, setStoredFilters] = useState(getStoredFilters);

  const [storedSorts, setStoredSorts] = useState(getStoredSorts);

  /**
   * Update the stored active preset and resets the stored values
   * recalculates the states
   */
  const setActivePreset = useCallback(
    (id: number, presets?: TablePreset[]) => {
      const paginationData = {
        page: defaultPage,
        pageSize: defaultPageSize,
        filters: defaultFilters,
        sortBy: defaultSorts,
      };
      if (id) {
        const preset = presets?.find(p => p.id === id);
        paginationData.filters = preset?.filters || [];
        paginationData.sortBy = preset?.sorts || [];
      }
      handlePaginationQueryParamsChange(paginationData);
      setActivePresetId(id || null);
    },
    [defaultPage, defaultPageSize, defaultFilters, defaultSorts, handlePaginationQueryParamsChange],
  );

  // Resets the stored pagination state if sportId changes
  useEffect(() => {
    if (
      sportId &&
      activePresetId &&
      presets.find(item => item.id === activePresetId)?.sportId !== sportId
    ) {
      setActivePreset(null);
    }
  }, [sportId, setActivePreset, presets, activePresetId]);

  useEffect(() => {
    setStorageValue(StorageKeys.SportId, sportId);
  }, [sportId]);

  useEffect(() => {
    storeActivePreset(key, activePresetId);
  }, [activePresetId, key]);

  const onAddPreset = useCallback(
    ({ columns, name }) => {
      const id = presets.length + 1;
      const updated = presets.concat({
        sportId,
        columns: columns.concat('id'),
        id,
        name,
        editable: true,
        filters: storedFilters,
        sorts: storedSorts,
      });
      setPresets(updated);
      setStorageValue(
        key,
        updated.filter(p => p.editable),
        StorageKeys.Presets,
      );
      setStorageValue(key, id, StorageKeys.ActivePreset);
      setActivePreset(id, updated);
    },
    [key, presets, setActivePreset, sportId, storedFilters, storedSorts],
  );

  const onRemovePreset = useCallback(
    id => {
      const updated = presets.filter(p => p.id !== id);
      setPresets(updated);
      setStorageValue(
        key,
        updated.filter(p => p.editable),
        StorageKeys.Presets,
      );

      setActivePreset(null);
    },
    [key, presets, setActivePreset],
  );

  useEffect(() => {
    if (disablePaginatedQueryParams) {
      setStoredFilters(storedFilters);
      setStoredSorts(storedSorts);

      return;
    }

    // Add pagination query params if query not exist
    if (!paginationQueryParams.toString()) {
      handlePaginationQueryParamsChange({
        page: getStorageValue(key, StorageKeys.Pagination)?.page || defaultPage,
        pageSize: getStorageValue(key, StorageKeys.Pagination)?.pageSize || defaultPageSize,
        filters: storedFilters,
        sortBy: storedSorts,
      });
      return;
    }

    const pageParam = paginationQueryParams.get(PaginationParams.Page);
    const pageSizeParam = paginationQueryParams.get(PaginationParams.PageSize);

    // Add page/pageSize if params not exist
    if (!pageParam || !pageSizeParam) {
      handlePaginationQueryParamsChange({
        page: !pageParam && (getStorageValue(key, StorageKeys.Pagination)?.page || defaultPage),
        pageSize:
          !pageSizeParam &&
          (getStorageValue(key, StorageKeys.Pagination)?.pageSize || defaultPageSize),
      });
      return;
    }

    const filtersParam = paginationQueryParams.get(PaginationParams.Filter);
    const sortByParam = paginationQueryParams.get(PaginationParams.SortBy);

    if (filtersParam) {
      const formattedFilters = mapIngestionStatusFiltersToDescriptions(
        filterQueryParamsParse(filtersParam),
      );

      if (!isEqual(formattedFilters, storedFilters)) {
        setStoredFilters(formattedFilters);
      }
    } else if (!filtersParam && storedFilters?.length !== 0) {
      setStoredFilters([]);
    }

    if (sortByParam) {
      const formattedSortBy = sortByQueryParamsParse(sortByParam);

      if (!isEqual(formattedSortBy, storedSorts)) {
        setStoredSorts(formattedSortBy);
      }
    } else if (!sortByParam && storedSorts?.length !== 0) {
      setStoredSorts([]);
    }
  }, [
    defaultPage,
    defaultPageSize,
    disablePaginatedQueryParams,
    filterQueryParamsParse,
    handlePaginationQueryParamsChange,
    key,
    mapIngestionStatusFiltersToDescriptions,
    paginationQueryParams,
    sortByQueryParamsParse,
    storedFilters,
    storedSorts,
  ]);

  const modifiedColumns = useMemo(() => {
    const activePreset = presets.find(p => p.id === activePresetId);
    const presetColumns = activePreset?.sportId
      ? activePreset.sportId === sportId
        ? activePreset.columns
        : null
      : activePreset?.columns;

    let cloned = cloneDeep(columns);

    if (presetColumns) {
      const columnsMap = cloned.reduce((acc, col) => {
        acc[col.id] = col;
        return acc;
      }, {});

      const idColumn = cloned.find(col => col.id === 'id');
      const actionsColumn = cloned.find(col => col.id === 'actions');

      cloned = [];
      if (idColumn) {
        cloned.push(idColumn);
      }

      const presetCloned = presetColumns
        .filter(id => columnsMap[id] && id !== 'id')
        .map(id => columnsMap[id]);

      cloned = [...cloned, ...presetCloned];

      if (actionsColumn) {
        cloned.push(actionsColumn);
      }
    } else {
      const idColumn = cloned.find(col => col.id === 'id');
      if (idColumn) {
        cloned = [idColumn, ...cloned.filter(col => col.id !== 'id')];
      }
    }

    const dualMultiselectFilters = storedFilters?.filter(({ columnId }) => cloned.some(column =>
        column.filters?.some(
          filter => filter.type === 'dual-multiselect' && filter.columnIds?.includes(columnId),
        ),
      ));

    if (dualMultiselectFilters && dualMultiselectFilters.length > 0) {
      const processedColumns = new Set<number>();

      dualMultiselectFilters.forEach(({ columnId }) => {
        const columnIndex = cloned.findIndex(column =>
          column.filters?.some(
            filter => filter.type === 'dual-multiselect' && filter.columnIds?.includes(columnId),
          ),
        );

        if (columnIndex !== -1 && !processedColumns.has(columnIndex)) {
          processedColumns.add(columnIndex);
          const filterIndex = cloned[columnIndex].filters.findIndex(
            f => f.type === 'dual-multiselect',
          );
          if (filterIndex !== -1) {
            const filter = cloned[columnIndex].filters[filterIndex];
            filter.conditions = filter.conditions.filter(
              c => !filter.columnIds || !filter.columnIds.includes(c.key),
            );
          }
        }
      });
    }

    storedFilters?.forEach(({ columnId, condition, value }) => {
      let columnIndex = cloned.findIndex(column => column.id === columnId);

      let isDualMultiselect = false;
      if (columnIndex === -1) {
        columnIndex = cloned.findIndex(column =>
          column.filters?.some(
            filter => filter.type === 'dual-multiselect' && filter.columnIds?.includes(columnId),
          ),
        );
        isDualMultiselect = columnIndex !== -1;
      }

      if (columnIndex !== -1) {
        if (isDualMultiselect) {
          const filterIndex = cloned[columnIndex].filters.findIndex(
            f => f.type === 'dual-multiselect',
          );
          if (filterIndex !== -1) {
            const filter = cloned[columnIndex].filters[filterIndex];
            let conditionIndex = filter.conditions.findIndex(c => c.key === columnId);

            if (conditionIndex === -1) {
              filter.conditions.push({
                key: columnId,
                label: columnId,
                value: '',
              });
              conditionIndex = filter.conditions.length - 1;
            }

            cloned[columnIndex].filters[filterIndex].conditions[conditionIndex].value = value;
          }
        } else {
          const filterIndex = cloned[columnIndex].filters.findIndex(f =>
            f.conditions.some(({ key }) => key === condition),
          );
          if (filterIndex !== -1) {
            const conditionIndex = cloned[columnIndex].filters[filterIndex].conditions.findIndex(
              c => c.key === condition,
            );
            cloned[columnIndex].filters[filterIndex].conditions[conditionIndex].value = value;
          }
        }
      }
    });

    storedSorts?.forEach(({ columnId, order }) => {
      const columnIndex = cloned.findIndex(column => column.id === columnId);
      if (columnIndex !== -1) {
        cloned[columnIndex].order = order;
      }
    });

    if (!disableControl) {
      cloned[cloned.length - 1].control = (
        <>
          <TablePresets
            presets={presets.filter(preset => (preset.sportId ? preset.sportId === sportId : true))}
            value={activePresetId || ''}
            onAddPreset={onAddPreset}
            onRemovePreset={onRemovePreset}
            onSelectPreset={setActivePreset}
            columns={columns
              .filter(column => column.id !== 'id' && column.id !== 'actions')
              .map(({ id, label }) => ({ name: label, value: id }))}
          />
        </>
      );
    }

    return cloned;
  }, [
    activePresetId,
    columns,
    presets,
    storedFilters,
    storedSorts,
    sportId,
    setActivePreset,
    onRemovePreset,
    onAddPreset,
    disableControl,
  ]);

  return {
    activePreset: activePresetId,
    columns: modifiedColumns,
    page:
      Number(paginationQueryParams.get(PaginationParams.Page)) ||
      getStorageValue(key, StorageKeys.Pagination)?.page ||
      defaultPage,
    pageSize:
      Number(paginationQueryParams.get(PaginationParams.PageSize)) ||
      getStorageValue(key, StorageKeys.Pagination)?.pageSize ||
      defaultPageSize,
    filters: storedFilters,
    setFilters: setStoredFilters,
    orders: storedSorts,
    setOrders: setStoredSorts,
    setActivePreset,
  };
};
